/**
 * Guias (`/guias/[slug]`): a busca de quem acabou de ter o problema ("cliente
 * faltou", "aluno não pagou"). Formato de resposta: resposta curta no topo,
 * cada seção com o título em pergunta e a resposta na 1ª frase (`capsule`),
 * modelo de mensagem pronto para copiar e, separado do conselho geral, o que a
 * Controlapag faz (`product`), com o número real do app.
 *
 * Lei: onde o tema toca direito do consumidor ou contrato, o texto cita a lei e
 * o artigo em `legalSources` (link do planalto.gov.br) e o último parágrafo da
 * seção é `LEGAL_DISCLAIMER`. Sem revisão jurídica ainda (D7): nada de
 * "revisado por". Número de produto vem de `content/facts.ts`.
 */

import { billableHoursPerMonth, computeHourlyRate, HOURLY_RATE_EXAMPLE } from '../../lib/hourly-rate';
import { formatBRL, FREE_PLAN, PACK_DEFAULTS, PROSE, REMINDERS } from '../facts';

export const LEGAL_DISCLAIMER = 'Este conteúdo não é orientação jurídica.';

export interface LegalSource {
  /** Nome da lei ("Código de Defesa do Consumidor"). */
  law: string;
  /** Artigo citado ("art. 52, § 1º"). */
  article: string;
  /** Texto oficial no planalto.gov.br. */
  url: string;
}

export interface GuideSection {
  /** A pergunta como a pessoa faz (termina em "?" quando há `capsule`). */
  heading: string;
  /** Resposta da pergunta em até 25 palavras, sem link. Vem logo abaixo do título. */
  capsule?: string;
  paragraphs?: string[];
  steps?: string[];
  /** Mensagem pronta para copiar (WhatsApp). */
  template?: string;
  /** Leis citadas na seção. Com fonte, o último parágrafo é `LEGAL_DISCLAIMER`. */
  legalSources?: LegalSource[];
}

/** "Como a Controlapag faz isso?": o produto, separado do conselho geral. */
export interface GuideProduct {
  capsule: string;
  steps: string[];
  /** O que disso é pago. Fica sempre à vista. */
  paidNote: string;
}

export const GUIDE_PRODUCT_HEADING = 'Como a Controlapag faz isso?';

/** Momento do profissional em que o guia é buscado (agrupa o hub `/guias`). */
export type GuideMoment = 'antes' | 'falta' | 'pagamento' | 'preco';

/** Título-base e descrição do hub `/guias`. */
export const GUIDES_HUB_TITLE = 'Guias para autônomos: falta, cobrança e preço';
export const GUIDES_HUB_DESCRIPTION =
  'Para quem atende com hora marcada: falta, cancelamento, mensalidade atrasada, lembrete, link de agendamento, quanto cobrar e como organizar pagamentos.';

export const GUIDE_MOMENTS: { id: GuideMoment; label: string }[] = [
  { id: 'antes', label: 'Antes do horário' },
  { id: 'falta', label: 'Quando o cliente falta' },
  { id: 'pagamento', label: 'Quando o cliente não paga' },
  { id: 'preco', label: 'Preço' },
];

export interface GuidePage {
  slug: string;
  /** Título-base, até 60 caracteres (`pageTitle()` decide o sufixo). */
  title: string;
  description: string;
  h1: string;
  /** A pergunta que o guia responde, como a pessoa pergunta (hub e `llms.txt`). */
  question: string;
  /** Resposta em 1 linha, para o hub. */
  oneLine: string;
  moment: GuideMoment;
  /** Resposta direta em 1 ou 2 frases, sem link. */
  answer: string;
  sections: GuideSection[];
  product: GuideProduct;
  faq: { question: string; answer: string }[];
  /** Ramos (`/para/[slug]`) em que o guia mais aparece. */
  segments: string[];
  /** Outros guias relacionados. */
  related: string[];
  /** Data da primeira publicação (AAAA-MM-DD). */
  publishedAt: string;
  /** Data da última revisão do texto (AAAA-MM-DD). Muda só quando o texto muda. */
  updatedAt: string;
}

const PLANALTO = {
  cdc: 'https://www.planalto.gov.br/ccivil_03/leis/l8078compilado.htm',
  cc: 'https://www.planalto.gov.br/ccivil_03/leis/2002/l10406compilada.htm',
  lei14905: 'https://www.planalto.gov.br/ccivil_03/_ato2023-2026/2024/lei/l14905.htm',
  lc123: 'https://www.planalto.gov.br/ccivil_03/leis/lcp/lcp123.htm',
};

const CDC = 'Código de Defesa do Consumidor (Lei 8.078/1990)';
const CC = 'Código Civil (Lei 10.406/2002)';

const LAW = {
  cdc6: { law: CDC, article: 'art. 6º, III', url: PLANALTO.cdc },
  cdc42: { law: CDC, article: 'art. 42', url: PLANALTO.cdc },
  cdc46: { law: CDC, article: 'art. 46', url: PLANALTO.cdc },
  cdc51: { law: CDC, article: 'art. 51, IV', url: PLANALTO.cdc },
  cdc52: { law: CDC, article: 'art. 52, § 1º', url: PLANALTO.cdc },
  cc406: { law: CC, article: 'art. 406', url: PLANALTO.cc },
  cc418: { law: CC, article: 'art. 418', url: PLANALTO.cc },
  lei14905: { law: 'Lei 14.905/2024', article: 'nova redação do art. 406 do Código Civil', url: PLANALTO.lei14905 },
  lc123: { law: 'Lei Complementar 123/2006 (Simples Nacional)', article: 'anexo III', url: PLANALTO.lc123 },
} satisfies Record<string, LegalSource>;

const h = (n: number) => `${n} ${n === 1 ? 'hora' : 'horas'}`;

/** Prazos para desmarcar que o assistente sugere, por ramo. */
const CANCEL_DEFAULTS = `${h(PACK_DEFAULTS.beleza.cancelLeadHours)} na beleza, ${h(
  PACK_DEFAULTS.personalTrainer.cancelLeadHours,
)} para personal trainer e professor particular e ${h(PACK_DEFAULTS.psicologo.cancelLeadHours)} para psicólogo e fisioterapeuta`;

const REMINDER_LEAD = h(REMINDERS.appointmentDefaultLeadHours);
const CHARGE_DAYS = REMINDERS.chargeDaysBeforeOptions.join(' ou ');
const FREE_LINE = `Na conta grátis você tem agenda, cadastro de clientes e cobranças avulsas para até ${FREE_PLAN.maxClientsPerMonth} clientes por mês.`;
const FEES = `${PROSE.pixFee}, ${PROSE.cardFee} e ${PROSE.boletoFee}`;

// Exemplo da calculadora, o mesmo que a tela abre preenchido.
const EX = HOURLY_RATE_EXAMPLE;
const EX_HOURS = billableHoursPerMonth(EX) ?? 0;
const EX_RATE = computeHourlyRate(EX) ?? 0;
const num = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
const EX_WEEKS = 52 - EX.weeksOff;

export const GUIDE_PAGES: GuidePage[] = [
  {
    slug: 'cliente-faltou-sem-avisar',
    title: 'Cliente faltou sem avisar: o que fazer',
    description:
      'O que fazer quando o cliente não aparece, quando dá para cobrar a falta pela lei, a mensagem pronta para mandar e como evitar a próxima falta.',
    h1: 'Cliente faltou sem avisar: o que fazer agora',
    question: 'O cliente faltou sem avisar, o que eu faço?',
    oneLine: 'Mande uma mensagem sem acusação oferecendo remarcar e anote a falta; cobrar só vale com regra combinada antes.',
    moment: 'falta',
    answer:
      'Mande uma mensagem curta e sem acusação oferecendo remarcar e anote a falta no histórico do cliente. Cobrar a falta só vale se a regra foi combinada antes e o valor é proporcional; para as próximas, mande 1 lembrete antes do horário e deixe a política de cancelamento por escrito.',
    sections: [
      {
        heading: 'O que fazer quando o cliente não aparece?',
        capsule:
          'Espere a tolerância que você avisou, mande uma mensagem educada oferecendo remarcar, libere o horário e anote a falta no histórico.',
        steps: [
          'Espere a tolerância que você já avisou antes (por exemplo, 15 minutos).',
          'Mande uma mensagem só, educada, perguntando se está tudo bem e oferecendo remarcar.',
          'Libere o horário: se tiver cliente esperando encaixe, avise.',
          'Anote a falta no histórico do cliente para saber se é a primeira ou se já virou hábito.',
        ],
        template:
          'Oi, [nome]! Tudo bem? Estava te esperando às [hora] para o [serviço]. Aconteceu alguma coisa? Se quiser, já te passo os próximos horários livres para remarcar.',
      },
      {
        heading: 'Dá para cobrar a falta do cliente?',
        capsule:
          'Só se a regra foi combinada antes, o cliente teve acesso a ela e o valor é proporcional ao horário perdido.',
        paragraphs: [
          'Regra que o cliente não conheceu antes de marcar não vale contra ele: o Código de Defesa do Consumidor diz que o contrato não obriga o consumidor quando ele não teve a chance de conhecer o conteúdo antes (art. 46). Por isso a regra precisa estar na mensagem de confirmação, na página de agendamento ou no contrato.',
          'O valor também precisa ser razoável. Cláusula que deixa o cliente em desvantagem exagerada é abusiva (art. 51, IV), então cobrar o serviço inteiro por qualquer falta é arriscado. Uma parte do valor, proporcional ao horário que você perdeu, se sustenta melhor.',
          'Outro caminho é pedir um sinal ao marcar. Se o cliente desiste sem justa causa, quem recebeu o sinal pode ficar com ele (Código Civil, art. 418).',
          LEGAL_DISCLAIMER,
        ],
        legalSources: [LAW.cdc46, LAW.cdc51, LAW.cc418],
      },
      {
        heading: 'Como evitar a próxima falta?',
        capsule:
          'Mande 1 lembrete antes do horário, deixe a política de cancelamento visível na hora de marcar e dê horário fixo a quem é recorrente.',
        steps: [
          'Um lembrete antes do horário, com dia, hora e endereço.',
          'Política de cancelamento curta e visível na hora de marcar (por exemplo, remarcação sem custo com até 24 horas de antecedência).',
          'Pagamento antes do atendimento para quem já faltou 2 vezes, combinado com o cliente.',
          'Horário fixo para cliente recorrente: quem tem "o seu horário" planeja a semana em volta dele.',
        ],
      },
    ],
    product: {
      capsule: `A Controlapag manda 1 lembrete por agendamento no WhatsApp e deixa você marcar a falta em até ${REMINDERS.noShowMarkWithinDays} dias depois do horário.`,
      steps: [
        `Lembrete: 1 por agendamento, pelo WhatsApp, com a antecedência que você escolhe (${REMINDER_LEAD} por padrão).`,
        `Prazo para desmarcar: dentro dele, o cliente não cancela nem remarca pelo app e precisa falar com você. O assistente sugere ${CANCEL_DEFAULTS}.`,
        `Falta: você marca em até ${REMINDERS.noShowMarkWithinDays} dias depois do horário, e ela fica no histórico do cliente.`,
        'Mensalidade com cota de sessões: a falta gasta a sessão do período; o cancelamento feito dentro do prazo devolve.',
        'O app não segura o horário até o cliente pagar. No avulso, a cobrança é criada com vencimento no dia do horário.',
      ],
      paidNote: `Lembrete no WhatsApp, perfil público com agenda e mensalidade fazem parte dos planos pagos, ${PROSE.fromPrice}. ${FREE_LINE}`,
    },
    faq: [
      {
        question: 'Quanto tempo esperar o cliente atrasado?',
        answer:
          'Escolha um número, como 15 minutos, e avise na confirmação do horário. Depois disso você libera o horário, e o cliente já sabia da regra.',
      },
      {
        question: 'Como cobrar sinal no agendamento?',
        answer: `Combine o valor do sinal ao marcar e mande a chave Pix junto com a confirmação. Se o cliente desistir sem justa causa, quem recebeu pode ficar com o sinal (Código Civil, art. 418). A Controlapag não segura o horário até o pagamento: no avulso, a cobrança é criada com vencimento no dia do horário. ${LEGAL_DISCLAIMER}`,
      },
      {
        question: 'Posso cobrar o valor inteiro do serviço quando o cliente falta?',
        answer: `É arriscado. O Código de Defesa do Consumidor considera abusiva a cláusula que deixa o cliente em desvantagem exagerada (art. 51, IV). Uma parte do valor, proporcional ao horário perdido e combinada antes, se sustenta melhor. ${LEGAL_DISCLAIMER}`,
      },
    ],
    segments: ['barbearia', 'manicure', 'psicologo', 'esteticista'],
    related: ['cliente-cancelou-em-cima-da-hora', 'mensagem-lembrete-de-horario'],
    publishedAt: '2026-09-28',
    updatedAt: '2026-10-03',
  },
  {
    slug: 'cliente-cancelou-em-cima-da-hora',
    title: 'Cliente cancelou em cima da hora: pode cobrar?',
    description:
      'Quando a lei deixa cobrar o cancelamento de última hora, como fazer uma política de cancelamento em 4 linhas e a mensagem para responder sem perder o cliente.',
    h1: 'Cliente cancelou em cima da hora: posso cobrar?',
    question: 'Cliente cancelou em cima da hora, posso cobrar?',
    oneLine: 'Pode, se a regra foi combinada antes, o cliente conheceu e o valor é proporcional ao horário perdido.',
    moment: 'falta',
    answer:
      'Pode, se a regra foi combinada antes, o cliente teve acesso a ela e o valor é proporcional ao horário perdido. Sem regra prévia, ofereça remarcar e crie a política a partir de agora, com prazo claro para desmarcar sem custo.',
    sections: [
      {
        heading: 'Como responder o cliente que cancelou em cima da hora?',
        capsule:
          'Agradeça o aviso, ofereça 2 horários e, se já existe política combinada, relembre a regra sem tom de cobrança.',
        paragraphs: ['Avisar é melhor que faltar. Se a política existe, cite a regra uma vez e de forma neutra.'],
        template:
          'Que bom que você avisou, [nome]! Tenho [dia] às [hora] ou [dia] às [hora]. Qual fica melhor? Só lembrando que, como combinamos, cancelamento com menos de [prazo] de antecedência tem a taxa de [valor].',
      },
      {
        heading: 'Como escrever uma política de cancelamento?',
        capsule:
          'Em 4 linhas: prazo para remarcar sem custo, o valor de quem cancela tarde, o que acontece na falta e a tolerância de atraso.',
        steps: [
          'Remarcação sem custo com até [prazo] de antecedência.',
          'Cancelamento com menos de [prazo]: [X]% do serviço, proporcional ao horário perdido.',
          'Falta sem aviso: [X]% do serviço, proporcional ao horário perdido, ou pagamento antes do próximo horário.',
          'Atraso acima de [15] minutos pode reduzir o tempo do atendimento.',
        ],
      },
      {
        heading: 'A lei deixa cobrar o cancelamento de última hora?',
        capsule:
          'Deixa, quando a regra foi combinada antes, o cliente teve acesso a ela e o valor cobrado é proporcional, não o serviço inteiro.',
        paragraphs: [
          'O Código de Defesa do Consumidor diz que o contrato não obriga o consumidor quando ele não teve a chance de conhecer o conteúdo antes (art. 46). A regra de cancelamento precisa chegar ao cliente antes de ele marcar.',
          'Cláusula que deixa o cliente em desvantagem exagerada é abusiva (art. 51, IV). Uma porcentagem proporcional ao horário perdido se sustenta melhor do que cobrar o serviço inteiro.',
          'Se você pediu sinal ao marcar e o cliente desiste sem justa causa, quem recebeu pode ficar com o sinal (Código Civil, art. 418).',
          LEGAL_DISCLAIMER,
        ],
        legalSources: [LAW.cdc46, LAW.cdc51, LAW.cc418],
      },
      {
        heading: 'Onde deixar a política de cancelamento?',
        capsule:
          'Na página de agendamento, na mensagem de confirmação e no contrato do cliente recorrente, para ele ver a regra antes de marcar.',
        paragraphs: ['Regra que o cliente nunca viu gera discussão na hora de cobrar e pode não valer.'],
      },
    ],
    product: {
      capsule:
        'Na Controlapag você define o prazo para desmarcar; dentro dele, o cliente não cancela nem remarca pelo app e precisa falar com você.',
      steps: [
        `O assistente da criação da conta sugere o prazo do seu ramo: ${CANCEL_DEFAULTS}. Você muda quando quiser.`,
        'Fora do prazo, o cliente cancela ou remarca sozinho em horário livre.',
        'Mensalidade com cota de sessões: o cancelamento dentro do prazo devolve a sessão; a falta gasta.',
        'O app não lança taxa de cancelamento sozinho. Se a sua política prevê, você cria uma cobrança avulsa para o cliente.',
      ],
      paidNote: `O cliente marcar, cancelar e remarcar sozinho depende do perfil público, que faz parte dos planos pagos, ${PROSE.fromPrice}. ${FREE_LINE}`,
    },
    faq: [
      {
        question: 'Qual antecedência pedir para cancelamento?',
        answer: `Depende do ramo e da duração do atendimento. O assistente da Controlapag sugere ${CANCEL_DEFAULTS}. Procedimento longo ou caro pede prazo maior.`,
      },
      {
        question: 'Cobrar taxa de cancelamento afasta cliente?',
        answer:
          'Quando a regra está escrita desde o primeiro horário e existe opção de remarcar sem custo dentro do prazo, a taxa só aparece para quem cancela tarde, e a conversa fica sobre a regra combinada, não sobre você.',
      },
    ],
    segments: ['salao-de-beleza', 'esteticista', 'psicologo', 'fisioterapeuta'],
    related: ['cliente-faltou-sem-avisar', 'mensagem-lembrete-de-horario'],
    publishedAt: '2026-09-28',
    updatedAt: '2026-10-03',
  },
  {
    slug: 'como-cobrar-mensalidade-atrasada',
    title: 'Mensalidade atrasada: como cobrar com educação',
    description:
      'Régua de cobrança, mensagem pronta e o que a lei diz sobre multa de 2% e juros para cobrar aluno ou cliente com mensalidade atrasada sem perder a relação.',
    h1: 'Como cobrar mensalidade atrasada sem constrangimento',
    question: 'Como cobrar mensalidade atrasada sem constrangimento?',
    oneLine: 'Lembrete 3 dias antes e no dia do vencimento; depois disso, uma mensagem sua, neutra e em particular.',
    moment: 'pagamento',
    answer:
      'Cobre cedo e por escrito: lembrete 3 dias antes com a forma de pagar, aviso no dia do vencimento e, depois que venceu, uma mensagem sua, neutra e em particular. Multa só vale se combinada antes e, para cliente pessoa física, vai até 2%.',
    sections: [
      {
        heading: 'Como montar a régua de cobrança da mensalidade?',
        capsule:
          'Lembrete 3 dias antes com a forma de pagar, aviso no dia do vencimento e, depois que venceu, uma conversa sua, gentil e por escrito.',
        steps: [
          '3 dias antes: lembrete com o valor, o vencimento e a chave Pix ou o link de pagamento.',
          'No dia: aviso curto de que vence hoje.',
          'Depois do vencimento, a conversa é sua: uma mensagem neutra perguntando se houve algum problema.',
          'Se o atraso passa de [10] dias: conversa direta sobre pausar os atendimentos até regularizar, se isso está no combinado.',
        ],
      },
      {
        heading: 'Que mensagem mandar para cobrar a mensalidade atrasada?',
        capsule:
          'Uma mensagem curta, com o mês, o valor, o vencimento e a forma de pagar, que deixe a porta aberta para quem já pagou.',
        template:
          'Oi, [nome]! Passando para lembrar que a mensalidade de [mês], de R$ [valor], venceu no dia [dia]. Você pode pagar pelo Pix [chave] ou pelo link [link]. Se já pagou, me avisa que eu atualizo aqui. Obrigado!',
      },
      {
        heading: 'Como cobrar sem constranger o cliente?',
        capsule:
          'Cobre em conversa particular, sem expor a dívida para outras pessoas e sem ameaça: a lei proíbe constranger o consumidor na cobrança.',
        paragraphs: [
          'O Código de Defesa do Consumidor diz que, na cobrança de dívidas, o consumidor não pode ser exposto ao ridículo nem sofrer constrangimento ou ameaça (art. 42). Na prática: nada de cobrar em grupo, na frente de outros clientes ou em post, nem de mandar mensagem com tom de ameaça.',
          'Se cobrou um valor que o cliente não devia e ele pagou, o mesmo artigo manda devolver em dobro, salvo engano justificável. Confira antes de cobrar.',
          LEGAL_DISCLAIMER,
        ],
        legalSources: [LAW.cdc42],
      },
      {
        heading: 'Posso cobrar multa e juros da mensalidade atrasada?',
        capsule:
          'Multa só se combinada antes, até 2% para cliente pessoa física. Juros de mora pela taxa legal valem mesmo sem cláusula.',
        paragraphs: [
          'Multa: precisa estar no contrato ou no combinado escrito antes do atraso. Quando o cliente é pessoa física, o Código de Defesa do Consumidor limita a multa por atraso a 2% do valor da parcela (art. 52, § 1º). Numa mensalidade de R$ 200, a multa máxima é R$ 4.',
          'Juros de mora: são devidos mesmo sem cláusula, pela taxa legal do Código Civil (art. 406, com a redação da Lei 14.905/2024), que é a taxa Selic menos a inflação medida pelo IPCA. Juros maiores que a taxa legal só valem se estiverem no contrato.',
          'Aplicar multa ou juros que o cliente não conhecia gera conflito e pode não valer.',
          LEGAL_DISCLAIMER,
        ],
        legalSources: [LAW.cdc52, LAW.cc406, LAW.lei14905],
      },
      {
        heading: 'O que fazer quando o atraso vira hábito?',
        capsule:
          'Mude o vencimento para logo depois do dia em que o cliente recebe e deixe no contrato o que acontece com os atendimentos quando atrasa.',
      },
    ],
    product: {
      capsule: `Na Controlapag, a mensalidade gera a cobrança todo mês e o lembrete sai no WhatsApp ${CHARGE_DAYS} dias antes e no dia do vencimento.`,
      steps: [
        `Lembrete de cobrança ${CHARGE_DAYS} dias antes do vencimento (ou nos dias que você escolher) e no dia, com valor, vencimento e link.`,
        'Depois do vencimento, o app não manda mais nada: a cobrança aparece no painel de quem falta pagar, e a conversa é sua.',
        'Quando o cliente paga direto a você, toque em "Confirmar recebimento" e confirme.',
        `Pelo link, o cliente paga com Pix, cartão ou boleto (se você ligar), com taxa por pagamento: ${FEES}.`,
      ],
      paidNote: `Mensalidade, lembrete no WhatsApp e recebimento pelo link fazem parte dos planos pagos, ${PROSE.fromPrice}. ${FREE_LINE}`,
    },
    faq: [
      {
        question: 'Posso cobrar juros da mensalidade atrasada?',
        answer: `Os juros de mora pela taxa legal (Selic menos IPCA) valem mesmo sem cláusula, pelo art. 406 do Código Civil, com a redação da Lei 14.905/2024. Juros maiores e multa só se estiverem no combinado escrito antes, e a multa vai até 2% quando o cliente é pessoa física (Código de Defesa do Consumidor, art. 52, § 1º). ${LEGAL_DISCLAIMER}`,
      },
      {
        question: 'Como automatizar a cobrança de alunos?',
        answer: `Cadastre cada aluno com o valor e o dia de vencimento. Nos planos pagos, a Controlapag gera a cobrança todo mês e manda o lembrete no WhatsApp com o link de pagamento, ${CHARGE_DAYS} dias antes e no dia do vencimento.`,
      },
    ],
    segments: ['personal-trainer', 'professor-particular', 'psicologo', 'fisioterapeuta', 'transporte-escolar'],
    related: ['como-organizar-clientes-e-pagamentos', 'mensagem-lembrete-de-horario', 'quanto-cobrar-por-hora', 'como-cobrar-as-familias-no-transporte-escolar'],
    publishedAt: '2026-09-28',
    updatedAt: '2026-10-03',
  },
  {
    slug: 'mensagem-lembrete-de-horario',
    title: 'Mensagem de lembrete de horário: 3 modelos',
    description:
      'Modelos de mensagem para o cliente no WhatsApp: confirmação logo depois de marcar, lembrete antes do horário e oferta de remarcação. Copie e adapte.',
    h1: 'Mensagem de lembrete de horário: modelos para copiar',
    question: 'Que mensagem mandar para lembrar o cliente do horário?',
    oneLine: 'Uma mensagem curta com serviço, dia, hora e endereço, pedindo uma resposta de uma palavra.',
    moment: 'antes',
    answer:
      'O lembrete que funciona é curto, tem serviço, dia, hora e endereço e pede uma resposta de uma palavra. Mande 1 lembrete por horário, 24 horas antes, e uma confirmação logo que o cliente marca.',
    sections: [
      {
        heading: 'O que escrever logo depois que o cliente marca?',
        capsule:
          'Confirme o serviço, o dia, a hora e o endereço numa mensagem só, e diga como avisar se precisar mudar.',
        template:
          'Oi, [nome]! Seu horário de [serviço] está marcado para [dia] às [hora], em [endereço]. Qualquer mudança é só me avisar por aqui.',
      },
      {
        heading: 'Que mensagem mandar para lembrar o cliente do horário?',
        capsule:
          'Uma mensagem curta, 24 horas antes, com o serviço, o dia, a hora e o endereço, pedindo uma resposta de uma palavra.',
        template:
          'Oi, [nome]! Lembrando do seu [serviço] amanhã às [hora], em [endereço]. Posso confirmar? Responda SIM ou, se precisar, te passo outro horário.',
      },
      {
        heading: 'Como oferecer outro horário quando o cliente precisa remarcar?',
        capsule:
          'Ofereça 2 horários concretos, em vez de perguntar quando a pessoa pode, e confirme o novo horário por escrito.',
        template:
          'Sem problema, [nome]! Tenho [dia] às [hora] ou [dia] às [hora]. Qual fica melhor? Assim que você escolher, eu confirmo por aqui.',
      },
      {
        heading: 'O que faz um lembrete funcionar?',
        capsule:
          'Ser curto, ter dia, hora e endereço, sair sempre com a mesma antecedência e pedir uma resposta simples.',
        steps: [
          'Uma mensagem por lembrete, sem textão.',
          'Sempre com dia, hora e endereço.',
          'Pergunta que se responde com uma palavra.',
          'Mesma antecedência para todo mundo: o cliente se acostuma.',
        ],
      },
    ],
    product: {
      capsule: `A Controlapag manda 1 lembrete por agendamento no WhatsApp, com a antecedência que você escolhe (${REMINDER_LEAD} por padrão).`,
      steps: [
        'O lembrete é só texto, com serviço, dia e hora; não leva Pix nem link.',
        `O assistente da criação da conta sugere a antecedência do seu ramo: ${h(PACK_DEFAULTS.beleza.reminderLeadHours)} para beleza, psicólogo e fisioterapeuta e ${h(PACK_DEFAULTS.personalTrainer.reminderLeadHours)} para personal trainer e professor particular.`,
        'Fora do prazo para desmarcar, o cliente remarca sozinho em horário livre.',
      ],
      paidNote: `Lembrete no WhatsApp e perfil público com agenda fazem parte dos planos pagos, ${PROSE.fromPrice}. ${FREE_LINE}`,
    },
    faq: [
      {
        question: 'Com quanta antecedência mandar o lembrete?',
        answer: `24 horas antes funciona para quem marca com dias de folga. O assistente da Controlapag sugere ${h(PACK_DEFAULTS.beleza.reminderLeadHours)} para beleza, psicólogo e fisioterapeuta e ${h(PACK_DEFAULTS.personalTrainer.reminderLeadHours)} para personal trainer e professor particular, e você ajusta.`,
      },
      {
        question: 'Tem como mandar lembrete automático no WhatsApp?',
        answer:
          'Tem, nos planos pagos da Controlapag: o lembrete sai sozinho para cada agendamento, 1 por horário, com a antecedência que você escolhe.',
      },
    ],
    segments: ['barbearia', 'salao-de-beleza', 'manicure', 'esteticista'],
    related: ['cliente-faltou-sem-avisar', 'cliente-cancelou-em-cima-da-hora', 'link-de-agendamento-para-autonomos'],
    publishedAt: '2026-09-28',
    updatedAt: '2026-10-03',
  },
  {
    slug: 'quanto-cobrar-por-hora',
    title: 'Quanto cobrar por hora: calculadora grátis',
    description:
      'Como calcular o valor da sua hora como autônomo: quanto quer ganhar, custos fixos, imposto, férias e horas que viram atendimento pago. Com calculadora.',
    h1: 'Quanto cobrar por hora como autônomo',
    question: 'Quanto cobrar por hora sendo autônomo?',
    oneLine: 'Renda desejada mais custos fixos, dividido por (1 − imposto) e pelas horas que você vende no mês.',
    moment: 'preco',
    answer: `Some quanto quer ganhar por mês com seus custos fixos, divida por (1 − imposto) e pelas horas que você vende no mês, já sem as férias. Com ${formatBRL(EX.income)} de renda, ${formatBRL(EX.costs)} de custos, ${EX.taxPercent}% de imposto e ${num(EX_HOURS)} horas vendáveis, a hora sai a ${formatBRL(EX_RATE)}.`,
    sections: [
      {
        heading: 'Como calcular o valor da hora de trabalho?',
        capsule:
          'Some o que quer ganhar e seus custos fixos, divida por (1 − imposto) e pelas horas vendidas no mês, sem as férias.',
        steps: [
          'Renda desejada por mês (o que sobra para você).',
          '+ custos fixos (aluguel, material, transporte, internet, aplicativos).',
          `÷ (1 − imposto): com ${EX.taxPercent}%, divida por ${num(1 - EX.taxPercent / 100)}.`,
          '÷ horas vendáveis por mês (a conta está na próxima pergunta).',
        ],
      },
      {
        heading: 'Quantas horas por mês você consegue vender?',
        capsule:
          'Horas por semana, vezes as semanas trabalhadas no ano, dividido por 12, vezes a parte do tempo que vira atendimento pago.',
        paragraphs: [
          `Com ${EX.hoursPerWeek} horas por semana, ${EX.weeksOff} semanas paradas por ano e ${EX.billablePercent}% do tempo em atendimento pago: ${EX.hoursPerWeek} × ${EX_WEEKS} ÷ 12 × ${num(EX.billablePercent / 100)} = ${num(EX_HOURS)} horas por mês.`,
          'O resto do tempo vai em deslocamento, faltas, intervalos, mensagens e administração. Conte uma semana sua de verdade antes de chutar a porcentagem.',
        ],
      },
      {
        heading: 'Que imposto colocar na conta?',
        capsule:
          'No MEI, o imposto é fixo: ponha nos custos fixos e deixe 0%. No Simples Nacional, anexo III, a 1ª faixa é 6%.',
        paragraphs: [
          'O MEI paga um valor fixo por mês, que não muda com o faturamento. Por isso ele entra junto com os custos fixos, e o campo de imposto fica em 0%.',
          'No Simples Nacional, os serviços do anexo III começam em 6% sobre o faturamento, na 1ª faixa (Lei Complementar 123/2006, anexo III). Em outra faixa ou outro regime, use a alíquota que o seu contador passar.',
          LEGAL_DISCLAIMER,
        ],
        legalSources: [LAW.lc123],
      },
      {
        heading: 'Quais erros fazem o autônomo cobrar pouco por hora?',
        capsule:
          'Dividir pelas horas trabalhadas em vez das vendidas, esquecer as férias, copiar o preço de outro profissional e ficar mais de 1 ano sem reajuste.',
        steps: [
          'Dividir pelas horas trabalhadas, não pelas vendidas.',
          `Esquecer férias e dias parados: a calculadora já tira ${EX.weeksOff} semanas por ano.`,
          'Copiar o preço de outro profissional sem saber o custo dele.',
          'Ficar mais de 1 ano sem reajustar o preço.',
        ],
      },
    ],
    product: {
      capsule:
        'Na Controlapag você lança as despesas do trabalho e o app mostra o lucro do mês, para conferir se o preço da hora está se pagando.',
      steps: [
        'Cadastre o serviço com o preço que saiu da conta.',
        'Lance as despesas (material, transporte, aluguel) no mesmo lugar das cobranças.',
        'O painel mostra "Recebido no mês" e "A receber", e o app calcula o lucro do mês.',
        'Na busca da Controlapag aparecem profissionais com perfil público na sua cidade, com o preço de partida de cada um.',
      ],
      paidNote: `Despesas, lucro do mês e cobranças avulsas estão na conta grátis, para até ${FREE_PLAN.maxClientsPerMonth} clientes por mês. Aparecer na busca com perfil público faz parte dos planos pagos, ${PROSE.fromPrice}.`,
    },
    faq: [
      {
        question: 'Quantas horas por mês um autônomo consegue vender?',
        answer: `Faça a conta com os seus números: horas por semana × (52 − semanas paradas) ÷ 12 × a parte do tempo que vira atendimento pago. Com ${EX.hoursPerWeek} horas por semana, ${EX.weeksOff} semanas paradas e ${EX.billablePercent}%, são ${num(EX_HOURS)} horas por mês.`,
      },
      {
        question: 'Sou MEI: que imposto coloco na calculadora?',
        answer:
          'Ponha o valor fixo mensal do MEI nos custos fixos e deixe o imposto em 0%. Assim ele entra na conta uma vez só.',
      },
      {
        question: 'Como comparar meu preço com o de outros profissionais?',
        answer:
          'Olhe profissionais do mesmo ramo e da mesma cidade. Na busca da Controlapag aparecem profissionais com perfil público na sua cidade, com o preço de partida de cada um.',
      },
    ],
    segments: ['personal-trainer', 'professor-particular', 'manicure', 'fisioterapeuta'],
    related: ['como-organizar-clientes-e-pagamentos', 'como-cobrar-mensalidade-atrasada'],
    publishedAt: '2026-09-28',
    updatedAt: '2026-10-03',
  },
  {
    slug: 'como-organizar-clientes-e-pagamentos',
    title: 'Organizar clientes e pagamentos sendo autônomo',
    description:
      'Como saber quem pagou e quem falta pagar sem depender da memória: o que anotar de cada cliente, a rotina de 10 minutos e a hora de sair do caderno.',
    h1: 'Como organizar clientes e pagamentos sendo autônomo',
    question: 'Como organizar clientes e pagamentos sendo autônomo?',
    oneLine: 'Tudo num lugar só: cada cliente com valor e dia de pagar, e cada cobrança marcada como paga ou em aberto.',
    moment: 'pagamento',
    answer:
      'Junte tudo num lugar só: cada cliente com o serviço, o valor e o dia de pagar, e cada cobrança marcada como paga ou em aberto. Anote o pagamento no mesmo dia em que ele cai e reserve 10 minutos por semana para conferir quem falta pagar.',
    sections: [
      {
        heading: 'Por que fica difícil saber quem pagou?',
        capsule:
          'Porque o cliente está no WhatsApp, o horário no caderno e o pagamento no extrato do banco: cada coisa num lugar diferente.',
        paragraphs: [
          'Cada coisa está anotada, mas em lugares diferentes. Na hora de saber quem pagou setembro, você precisa juntar 3 lugares de cabeça.',
          'Organizar é tirar essa conta da memória e colocar num lugar só.',
        ],
      },
      {
        heading: 'O que anotar de cada cliente?',
        capsule: 'Nome e WhatsApp, o serviço que ele faz com você, o valor, o dia de pagar e como ele paga.',
        steps: [
          'Nome e WhatsApp.',
          'O serviço que ele faz com você (por exemplo, 4 cortes por mês, aula de terça e quinta, transporte de segunda a sexta).',
          'O valor e o dia de pagar.',
          'Como ele paga: Pix, dinheiro ou cartão.',
        ],
        paragraphs: ['Só isso. Ficha longa demais não é preenchida, e ficha que não é preenchida não ajuda.'],
      },
      {
        heading: 'Como saber, num olhar, quem falta pagar?',
        capsule:
          'Tenha uma lista de cobranças, uma linha por mês ou por atendimento, com valor, vencimento e situação: em aberto, pago ou atrasado.',
        paragraphs: [
          'É essa lista que responde quem falta pagar, e não o caderno de anotações.',
          'Se você cobra todo mês o mesmo valor, deixe a linha do mês seguinte pronta assim que o mês atual for pago. Assim nenhum cliente fica sem cobrança por esquecimento.',
        ],
      },
      {
        heading: 'Que rotina mantém os pagamentos em dia?',
        capsule:
          'Marque o pagamento no dia em que ele cai, confira o que venceu 1 vez por semana e feche o mês somando o que entrou.',
        steps: [
          'No dia em que o pagamento cair, marque como pago. Não deixe para o fim do mês.',
          '1 vez por semana, 10 minutos: olhe o que venceu e ainda está em aberto e mande o lembrete.',
          'No fim do mês: some o que entrou, tire as despesas do trabalho (material, transporte, aluguel) e veja quanto sobrou.',
        ],
        template:
          'Oi, [nome]! Tudo bem? Passando para lembrar do pagamento de [serviço], de R$ [valor], que venceu no dia [dia]. Se já pagou, me avisa que eu atualizo aqui. Obrigado!',
      },
      {
        heading: 'Quando o caderno deixa de dar conta?',
        capsule:
          'Quando você cobra quem já pagou, descobre um atraso antigo ou passa mais de 1 hora por semana conferindo pagamento.',
        steps: [
          'Você já cobrou alguém que tinha pago.',
          'Você descobriu um atraso de 2 meses sem perceber.',
          'Você não sabe dizer, sem fazer conta, quanto ganhou no mês passado.',
          'Você passa mais de 1 hora por semana conferindo pagamento.',
        ],
        paragraphs: ['Com 2 ou mais desses, um app feito para isso economiza o seu tempo.'],
      },
    ],
    product: {
      capsule:
        'Na Controlapag, cada cliente tem as cobranças com valor, vencimento e situação, e o painel mostra o que foi recebido no mês e o que falta receber.',
      steps: [
        'Na criação da conta, você conta com o que trabalha e o assistente monta os serviços e valores do seu ramo.',
        'Você cadastra os clientes, cria as cobranças e vê no painel "Recebido no mês" e "A receber".',
        'Quando o cliente paga direto a você, toque em "Confirmar recebimento" e confirme.',
        'As despesas entram no mesmo lugar e o app mostra o lucro do mês.',
      ],
      paidNote: `${FREE_LINE} Mensalidade que se repete sozinha, lembrete automático pelo WhatsApp e perfil público com agenda para o cliente marcar fazem parte dos planos pagos, ${PROSE.fromPrice}.`,
    },
    faq: [
      {
        question: 'Planilha ou app: qual é melhor para controlar clientes?',
        answer:
          'Planilha funciona com poucos clientes e se você abre ela todo dia. Um app ganha quando você está na rua, no celular, e precisa marcar o pagamento na hora, ou quando a cobrança se repete todo mês.',
      },
      {
        question: 'Como saber rápido quem está devendo?',
        answer:
          'Mantenha uma lista de cobranças com a situação de cada uma (em aberto, paga, atrasada) e marque o pagamento no mesmo dia. Num app como a Controlapag, o painel mostra quem falta pagar sem você fazer conta.',
      },
      {
        question: 'Meu cliente precisa baixar alguma coisa?',
        answer:
          'Não. Na Controlapag o cliente pode criar uma conta grátis para acompanhar cobranças e horários, mas não é obrigatório. Quem não quiser continua falando com você pelo WhatsApp.',
      },
      {
        question: 'Dá para começar de graça?',
        answer: `Sim. A conta grátis da Controlapag tem cadastro de clientes, agenda, cobranças avulsas e despesas para até ${FREE_PLAN.maxClientsPerMonth} clientes por mês.`,
      },
    ],
    segments: ['manicure', 'barbearia', 'personal-trainer', 'professor-particular', 'esteticista', 'diarista', 'transporte-escolar'],
    related: ['como-cobrar-mensalidade-atrasada', 'quanto-cobrar-por-hora'],
    publishedAt: '2026-10-01',
    updatedAt: '2026-10-03',
  },
  {
    slug: 'link-de-agendamento-para-autonomos',
    title: 'Link de agendamento: como o cliente marca sozinho',
    description:
      'Como deixar o cliente marcar horário sozinho: o que definir antes de abrir a agenda, o que o link da bio precisa ter e a mensagem para avisar os clientes.',
    h1: 'Link de agendamento: como deixar o cliente marcar horário sozinho',
    question: 'Como o cliente pode marcar horário sozinho?',
    oneLine: 'Uma página com seus serviços, preços e só os horários livres, no link da bio e no WhatsApp.',
    moment: 'antes',
    answer:
      'Com um link de agendamento: uma página com seus serviços, preços e só os horários livres, onde o cliente escolhe e marca sem trocar mensagem com você. Antes de divulgar, defina a duração de cada serviço, a antecedência para marcar e o prazo para desmarcar.',
    sections: [
      {
        heading: 'O que é um link de agendamento?',
        capsule:
          'Uma página com seus serviços, preços e horários livres, onde o cliente escolhe o horário e marca sem esperar você responder.',
        paragraphs: [
          'O cliente abre o link, escolhe o serviço, vê só o que está livre e marca. Você recebe o horário pronto na agenda.',
          'É diferente do link do WhatsApp (aquele wa.me com o seu número). Esse só abre uma conversa: você continua respondendo "tem horário?" um por um.',
        ],
      },
      {
        heading: 'O que definir antes de abrir a agenda para o cliente?',
        capsule:
          'Os dias e horários em que você atende, a duração de cada serviço, a antecedência mínima para marcar e o prazo para desmarcar.',
        steps: [
          'Dias e horários em que você atende. Só eles aparecem para o cliente.',
          'A duração de cada serviço, com o intervalo de que você precisa entre um cliente e outro.',
          'A antecedência mínima para marcar (por exemplo, 4 horas), para ninguém marcar daqui a 10 minutos.',
          `O prazo para desmarcar. O assistente da Controlapag sugere ${CANCEL_DEFAULTS}.`,
          'Até quantos dias à frente o cliente pode marcar.',
        ],
      },
      {
        heading: 'O que o link da bio precisa ter?',
        capsule:
          'Preço à vista, horário de verdade, como pagar e foto do seu trabalho. Sem preço, o cliente volta a perguntar no WhatsApp.',
        steps: [
          'Preço de cada serviço (ou "a partir de", quando varia).',
          'Só horários livres de verdade: horário que já está ocupado não pode aparecer.',
          'Como o cliente paga: Pix, dinheiro, cartão.',
          'Fotos do seu trabalho, para o cliente decidir sem perguntar.',
          'Onde você atende, ou se vai até o cliente.',
        ],
      },
      {
        heading: 'Como avisar os clientes que agora dá para marcar sozinho?',
        capsule:
          'Mande uma mensagem curta com o link, dizendo o que muda para o cliente: ele marca a hora que quiser, sem esperar você responder.',
        template:
          'Oi, [nome]! Agora você pode marcar seu horário sozinho, a qualquer hora, sem esperar eu responder: [link]. Lá aparecem os horários livres e os preços. Se precisar de algo, continuo por aqui.',
      },
      {
        heading: 'E o cliente que continua pedindo horário por mensagem?',
        capsule:
          'Responda com gentileza e mande o link junto. Depois de 2 ou 3 vezes, ele passa a marcar sozinho.',
        template:
          'Oi, [nome]! Os horários livres estão todos aqui, é só escolher: [link]. Se não achar um que dê certo, me fala que eu vejo com você.',
      },
    ],
    product: {
      capsule:
        'Na Controlapag, o perfil público é o seu link de agendamento: serviços, preços, fotos e a agenda mostrando só os horários livres.',
      steps: [
        'Na criação da conta, o assistente monta os serviços, os valores e a agenda do seu ramo. Você confere e ajusta.',
        'O cliente marca pelo link só com nome e WhatsApp, sem criar conta e sem baixar nada.',
        'Quem vem toda semana pode pedir pelo perfil um plano com horário fixo, e você aprova.',
        'O horário entra na agenda, o lembrete sai sozinho pelo WhatsApp e a cobrança fica no mesmo cliente.',
        'Pelo "Criar post", o app monta uma imagem com os seus horários livres da semana para divulgar.',
      ],
      paidNote: `Perfil público com agenda, lembrete no WhatsApp e plano mensal fazem parte dos planos pagos, ${PROSE.fromPrice}. ${FREE_LINE}`,
    },
    faq: [
      {
        question: 'Link do WhatsApp é a mesma coisa que link de agendamento?',
        answer:
          'Não. O link do WhatsApp só abre uma conversa com você, e você continua respondendo um por um. No link de agendamento, o cliente vê os horários livres e marca sozinho.',
      },
      {
        question: 'O cliente precisa criar conta para marcar?',
        answer:
          'Na Controlapag, não. Ele marca com nome e WhatsApp e confirma com um código. Se quiser, cria uma conta grátis para ver os horários, remarcar e acompanhar os pagamentos.',
      },
      {
        question: 'E se o cliente marcar num horário que eu não posso?',
        answer:
          'Ele só vê os horários em que você disse que atende e que ainda estão livres. Para tirar um dia ou um período, crie um bloqueio na agenda, e esses horários somem do link.',
      },
      {
        question: 'Onde coloco o link de agendamento?',
        answer:
          'Na bio do Instagram, na mensagem automática do WhatsApp Business e na resposta para quem pergunta horário. Quanto mais o cliente vê o link, menos ele pergunta.',
      },
    ],
    segments: ['manicure', 'barbearia', 'personal-trainer', 'professor-particular', 'esteticista', 'psicologo'],
    related: ['mensagem-lembrete-de-horario', 'cliente-cancelou-em-cima-da-hora', 'como-organizar-agenda-de-clientes'],
    publishedAt: '2026-10-06',
    updatedAt: '2026-10-06',
  },
  {
    slug: 'como-organizar-agenda-de-clientes',
    title: 'Como organizar a agenda de clientes',
    description:
      'Agenda sem horário em cima de horário: a duração real de cada serviço, o intervalo, a folga bloqueada antes e a mensagem para avisar feriado.',
    h1: 'Como organizar a agenda de clientes trabalhando sozinho',
    question: 'Como organizar a agenda de clientes?',
    oneLine: 'Horário de começar e de parar, a duração real de cada serviço com intervalo, e folga bloqueada antes de alguém marcar.',
    moment: 'antes',
    answer:
      'Defina os dias e horários em que você atende, anote a duração real de cada serviço com um intervalo entre um cliente e outro, e bloqueie folgas e feriados antes que alguém marque. Tudo num lugar só, e não metade no caderno e metade no WhatsApp.',
    sections: [
      {
        heading: 'Por que a agenda vive bagunçada?',
        capsule:
          'Porque o horário é combinado em vários lugares (conversa, caderno, cabeça) e nenhum deles mostra o dia inteiro de uma vez.',
        paragraphs: [
          'O cliente pede horário no WhatsApp, você confere no caderno e responde de cabeça. Quando 2 pessoas pedem ao mesmo tempo, o mesmo horário sai para as 2.',
          'Organizar a agenda é ter 1 lugar que mostra o dia inteiro, e só marcar olhando para ele.',
        ],
      },
      {
        heading: 'O que definir antes de marcar o próximo cliente?',
        capsule: 'Os dias e horários em que você atende, a duração de cada serviço e o intervalo entre um cliente e outro.',
        steps: [
          'Os dias da semana e as faixas de horário em que você atende (e o horário de parar).',
          'A duração real de cada serviço, cronometrada, e não a que você gostaria que fosse.',
          'Um intervalo entre um cliente e outro para atraso, limpeza ou deslocamento.',
          'Quanto tempo antes o cliente pode desmarcar.',
        ],
        paragraphs: ['Com isso definido, você para de responder "deixa eu ver aqui" e já sabe quais horários tem.'],
      },
      {
        heading: 'Como evitar 2 clientes no mesmo horário?',
        capsule: 'Marque tudo no mesmo lugar, na hora em que combina, e confirme o horário por escrito com o cliente.',
        steps: [
          'Nunca diga "pode vir" antes de olhar a agenda.',
          'Anote o horário no momento em que combinar, não depois.',
          'Responda com dia, hora e serviço por escrito. A mensagem vira o comprovante do combinado.',
        ],
        template: 'Combinado, [nome]! [Serviço] na [dia da semana], dia [data], às [hora]. Se precisar desmarcar, me avisa até [prazo]. Até lá!',
      },
      {
        heading: 'Como avisar os clientes de feriado ou folga?',
        capsule: 'Bloqueie o dia na agenda primeiro e avise os clientes com pelo menos 1 semana de antecedência, já oferecendo outro horário.',
        steps: [
          'Bloqueie o dia ou o período antes de avisar, para ninguém marcar no meio do caminho.',
          'Avise primeiro quem já está marcado naquele dia, oferecendo 2 opções de horário.',
          'Depois, avise todo mundo no status ou no story, com a data de volta.',
        ],
        template:
          'Oi, [nome]! Dia [data] não vou atender por causa do feriado. Seu horário de [hora] pode ir para [opção 1] ou [opção 2]. Qual fica melhor pra você?',
      },
      {
        heading: 'Que rotina mantém a agenda em dia?',
        capsule: 'Olhe o dia seguinte toda noite e a semana seguinte todo domingo.',
        steps: [
          'Toda noite, 2 minutos: confira os horários de amanhã e confirme com quem marcou.',
          'Todo domingo, 10 minutos: veja os horários livres da semana e divulgue.',
          'Depois de cada falta, anote. Quem falta 2 vezes combina a regra de novo.',
        ],
      },
    ],
    product: {
      capsule:
        'Na Controlapag, a agenda já sai da configuração com os seus dias, horários e a duração de cada serviço, e as folgas viram bloqueio.',
      steps: [
        'Em "Configurar agenda", na aba Horários, você liga os dias da semana e as faixas em que atende.',
        'Na aba Serviços, cada serviço tem a duração, e é dela que saem os horários.',
        'Na aba Bloqueios ("Folgas e imprevistos"), você fecha um dia inteiro ou um período, com o motivo.',
        'Com o perfil público, o cliente só vê os horários livres e não consegue marcar no dia bloqueado.',
      ],
      paidNote: `${FREE_LINE} Perfil público para o cliente marcar sozinho e lembrete de horário pelo WhatsApp fazem parte dos planos pagos, ${PROSE.fromPrice}.`,
    },
    faq: [
      {
        question: 'Agenda de papel ou agenda no celular?',
        answer:
          'O papel funciona enquanto você está perto dele. Quando o cliente pede horário com você na rua ou no meio de um atendimento, a agenda no celular evita o "te respondo depois" e o horário marcado em dobro.',
      },
      {
        question: 'Quanto de intervalo deixar entre um cliente e outro?',
        answer:
          'O suficiente para o atraso mais comum mais o que você faz entre um e outro (limpar, arrumar, se deslocar). Para muita gente fica entre 10 e 15 minutos. Cronometre uma semana e ajuste.',
      },
      {
        question: 'Com quanta antecedência avisar uma folga?',
        answer:
          'Pelo menos 1 semana, e antes de qualquer outra coisa bloqueie o dia na agenda. Quem já está marcado recebe a mensagem primeiro, com 2 opções de horário.',
      },
      {
        question: 'Dá para organizar a agenda de graça?',
        answer: `Sim. A conta grátis da Controlapag tem agenda, horários, bloqueios e cadastro de clientes para até ${FREE_PLAN.maxClientsPerMonth} clientes por mês.`,
      },
    ],
    segments: ['manicure', 'barbearia', 'personal-trainer', 'professor-particular', 'esteticista', 'psicologo'],
    related: ['link-de-agendamento-para-autonomos', 'mensagem-lembrete-de-horario', 'cliente-faltou-sem-avisar'],
    publishedAt: '2026-10-13',
    updatedAt: '2026-10-13',
  },
  {
    slug: 'como-cobrar-as-familias-no-transporte-escolar',
    title: 'Como cobrar as famílias no transporte escolar',
    description:
      'O que combinar com as famílias antes do ano começar (férias, irmãos, reajuste, dia de pagar) e a mensagem pronta para a mensalidade que atrasou.',
    h1: 'Como cobrar as famílias no transporte escolar sem virar briga',
    question: 'Como cobrar as famílias no transporte escolar?',
    oneLine: 'Combinado por escrito antes do ano (férias, irmãos, reajuste, vencimento) e cobrança no dia, sempre com o mesmo texto.',
    moment: 'pagamento',
    answer:
      'Combine por escrito, antes do ano letivo, o valor de cada rota, o dia de pagar, se as férias são cobradas, o desconto de irmãos e quando há reajuste. Depois, cobre todo mês no mesmo dia e com a mesma mensagem, para todas as famílias.',
    sections: [
      {
        heading: 'Por que a cobrança da van vira briga?',
        capsule:
          'Porque o combinado ficou de boca: cada família lembra de um valor, de um dia de pagar e de uma regra de férias diferente.',
        paragraphs: [
          'No transporte escolar, quem paga é o responsável, e você fala com ele quase todo dia na porta de casa. Cobrar parece pessoal.',
          'Quando o combinado está escrito e é igual para todos, a cobrança deixa de ser conversa e vira lembrete.',
        ],
      },
      {
        heading: 'O que combinar com as famílias antes do ano começar?',
        capsule: 'Valor de cada rota, dia de vencimento, regra das férias, desconto de irmãos, reajuste e aviso para sair.',
        steps: [
          'O valor de cada rota: ida e volta, só ida, só volta.',
          'O dia de pagar, igual para todas as famílias (ex.: todo dia 5).',
          'Se julho, dezembro e janeiro são cobrados, e quanto.',
          'O desconto para irmãos, se houver, por escrito.',
          'Quando e como a mensalidade é reajustada.',
          'Com quanta antecedência a família avisa que vai sair.',
        ],
        paragraphs: [
          'Os órgãos de defesa do consumidor entendem que a cobrança nas férias é válida quando a família foi avisada antes, de forma clara. Avisar depois é o que dá problema.',
          LEGAL_DISCLAIMER,
        ],
        legalSources: [LAW.cdc6],
      },
      {
        heading: 'Como avisar o combinado para as famílias?',
        capsule: 'Mande o mesmo texto para cada responsável, no privado, e peça uma resposta de confirmação.',
        steps: [
          'Mande no privado, não no grupo da van.',
          'Peça um "ok" de volta. A resposta vira o registro do combinado.',
          'Guarde o texto e a resposta de cada família.',
        ],
        template:
          'Oi, [nome]! Pra [ano], a mensalidade da [rota] do [aluno] fica R$ [valor], com vencimento todo dia [dia]. Julho e dezembro são cobrados [inteiro ou metade]. Irmãos têm [desconto]. Pode me confirmar com um ok?',
      },
      {
        heading: 'Como cobrar a mensalidade que atrasou?',
        capsule: 'Mande uma mensagem curta no dia seguinte ao vencimento, com valor e mês, e nunca envolva o aluno.',
        steps: [
          'No dia seguinte ao vencimento, mensagem curta com o mês e o valor.',
          'Sem resposta em 3 dias, ligue ou fale pessoalmente, longe do aluno.',
          'Se a família precisar de prazo, combine a nova data por escrito.',
        ],
        template:
          'Oi, [nome]! Passando pra lembrar da mensalidade de [mês] da van, R$ [valor], que venceu dia [dia]. Se já pagou, me manda o comprovante que eu dou baixa. Obrigado!',
        paragraphs: ['Nunca comente o atraso com a criança nem no grupo das famílias.'],
      },
      {
        heading: 'Como saber quem já pagou no mês?',
        capsule: 'Uma lista única com todas as famílias, o valor e o dia em que cada uma pagou, conferida no dia do vencimento.',
        steps: [
          'Uma linha por família, não por aluno: irmãos pagam juntos.',
          'Anote o pagamento no dia em que o Pix chega, com o comprovante.',
          'No dia seguinte ao vencimento, quem não tem data é quem você cobra.',
        ],
      },
    ],
    product: {
      capsule:
        'Na Controlapag, cada família tem as rotas com o valor, e o painel mostra quem pagou e quem falta no mês.',
      steps: [
        'O assistente já cria as rotas do transporte escolar: ida e volta, só ida, só volta, taxa de matrícula e passeio.',
        'Cada cobrança tem valor e vencimento. Pagou por fora, você toca em Confirmar recebimento e confirma.',
        'No painel, "Recebido no mês" e "A receber" mostram quanto falta.',
        `Nos planos pagos, a mensalidade sai de um contrato com o dia de vencimento, e o lembrete vai no WhatsApp do responsável ${CHARGE_DAYS} dias antes e no dia.`,
      ],
      paidNote: `${FREE_LINE} Mensalidade com contrato, lembrete no WhatsApp e recebimento pelo link (${FEES}) fazem parte dos planos pagos, ${PROSE.fromPrice}.`,
    },
    faq: [
      {
        question: 'O transporte escolar pode cobrar nas férias?',
        answer:
          'Pode, se a família foi avisada antes e por escrito, de preferência no contrato ou numa mensagem confirmada. Cobrar sem ter avisado é o que costuma ser contestado.',
      },
      {
        question: 'Irmãos pagam 2 mensalidades?',
        answer:
          'Depende do que você combinar. Muitos motoristas dão desconto para o segundo irmão. O que importa é estar escrito e valer igual para todas as famílias.',
      },
      {
        question: 'Posso deixar de levar o aluno se a família não pagar?',
        answer:
          'Antes de qualquer coisa, converse com a família e combine um prazo por escrito. A regra de suspensão, se houver, precisa estar no combinado desde o começo, e a cobrança nunca deve expor a criança.',
      },
      {
        question: 'Qual o melhor dia para o vencimento?',
        answer:
          'Um dia logo depois do pagamento da maioria das famílias, como 5 ou 10, e o mesmo para todas. Vencimento diferente por família é o que faz a conta do mês se perder.',
      },
    ],
    segments: ['transporte-escolar'],
    related: ['como-cobrar-mensalidade-atrasada', 'como-organizar-clientes-e-pagamentos'],
    publishedAt: '2026-10-19',
    updatedAt: '2026-10-19',
  },
];

export const findGuidePage = (slug: string): GuidePage | undefined =>
  GUIDE_PAGES.find((page) => page.slug === slug);

export const guidesByMoment = (moment: GuideMoment): GuidePage[] =>
  GUIDE_PAGES.filter((page) => page.moment === moment);
